/**
 * Дозагрузка папок живого эфира (каждые 20 с): что из ответа сервера ложится в редактор.
 *
 * Раньше дозагрузка только ДОПИСЫВАЛА новые реплики, а каждая прежняя оставалась такой, какой её
 * загрузили, — и та, которой на сервере уже нет. Проба 20261103-P50m10d, эфир 8a8caa32, папка 1
 * en: редактор загрузил папку в 21:06:48, до озвучки; раскладка по фразам основного прохода
 * (21:07:30–34) разрезала фразу 20261103210219895344 (134460, слот 4120 мс) на куски
 * …210731211881 (134460) и …210732417352 и унесла родителя в `delete/`. Дозагрузка дописала куски
 * рядом с родителем, которого на сервере уже не было, и со старыми длинами всех реплик (слоты до
 * озвучки). ♪ соседней реплики запустил по этой копии раздвижку редактора: кусок встал за конец
 * мёртвого родителя (138580 + 50 = 138630, на 4170 мс от слова лектора), за ним цепочкой ещё
 * шесть реплик, и автосохранение записало это на диск под именем переводчика в 21:08:14 — после
 * того как «Выравнивание» папки уже было «пройдено». Строгий шаг остановил эфир.
 *
 * Теперь реплика, которую человек не правит (нет несохранённой правки), берётся такой, какой её
 * прислал сервер, а пропавшая с сервера — уходит и из редактора. Несохранённая правка не трогается
 * ни у какой реплики: строка остаётся локальной, даже если сервер её уже не знает (её сохранение
 * тогда откажет с причиной — правка человека не теряется молча).
 */

// Папки и реплики — в том виде, в каком их отдаёт сервер (как и прежде, без строгого типа).
type Row = any;
type Folder = any;

export const liveRowId = (row: Row): string => String(row.id ?? row.original_id);

const sameRow = (a: Row, b: Row): boolean => {
    if (a === b) return true;
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
        return false;
    }
};

/**
 * Прежняя дозагрузка: только новые реплики дописываются, прежние остаются с прежними полями,
 * папки дальше конца — целиком. Нужна, когда, пока шёл запрос, в редакторе что-то менялось
 * (правка, сохранение): ответ мог уйти с сервера до записи, и брать из него прежние реплики нельзя.
 */
export function mergeSubtitleFoldersAdditive(prev: Folder[] | undefined, fresh: Folder[]): any[] {
    const prevArr = prev || [];
    const out: Folder[] = [];
    for (let i = 0; i < fresh.length; i++) {
        const ff = fresh[i] || {};
        const pf = prevArr[i];
        if (!pf) {
            out.push(ff); // brand-new folder
            continue;
        }
        const existing = new Set<string>((pf.translations || []).map(liveRowId));
        const added = (ff.translations || []).filter((t: Row) => !existing.has(liveRowId(t)));
        out.push(added.length ? { ...pf, translations: [...(pf.translations || []), ...added] } : pf);
    }
    for (let i = fresh.length; i < prevArr.length; i++) out.push(prevArr[i]);
    return out;
}

/**
 * Дозагрузка по серверу: реплика без несохранённой правки (`keepLocal(id)` — false) — такая, как на
 * сервере; пропавшая с сервера — уходит; с правкой — остаётся локальной. Неизменившаяся реплика и
 * неизменившаяся папка остаются теми же объектами: дорожки не перерисовываются каждые 20 с.
 */
export function mergeSubtitleFoldersLive(
    prev: Folder[] | undefined,
    fresh: Folder[],
    keepLocal: (id: string) => boolean,
): any[] {
    const prevArr = prev || [];
    const out: Folder[] = [];
    for (let i = 0; i < fresh.length; i++) {
        const ff = fresh[i] || {};
        const pf = prevArr[i];
        if (!pf) {
            out.push(ff);
            continue;
        }
        const prevRows = pf.translations || [];
        // Без кортежных типов: сборка редактора (CRA 3, eslint-парсер TS 3.4) падает на них при TypeScript 5.
        const prevById = new Map<string, Row>();
        prevRows.forEach((t: Row) => prevById.set(liveRowId(t), t));
        const freshRows = ff.translations || [];
        const freshIds = new Set<string>(freshRows.map(liveRowId));
        const rows: Row[] = freshRows.map((t: Row) => {
            const old = prevById.get(liveRowId(t));
            if (old && (keepLocal(liveRowId(t)) || sameRow(old, t))) return old;
            return t;
        });
        // Правка человека у реплики, которой сервер уже не прислал, не теряется.
        prevRows.forEach((t: Row) => {
            if (!freshIds.has(liveRowId(t)) && keepLocal(liveRowId(t))) rows.push(t);
        });
        const changed = rows.length !== prevRows.length || rows.some((r: Row, j: number) => r !== prevRows[j]);
        out.push(changed ? { ...pf, translations: rows } : pf);
    }
    for (let i = fresh.length; i < prevArr.length; i++) out.push(prevArr[i]);
    return out;
}

/** Есть ли у реплики `id` языка `lang` несохранённая правка (человека или машинный сдвиг). */
export const hasUnsavedChange = (dirtyKeys: Iterable<string>, lang: string, id: string): boolean => {
    const own = `${lang}:${id}`;
    for (const key of Array.from(dirtyKeys)) {
        if (key === own || key.startsWith(`${own}@`)) return true;
    }
    return false;
};
